import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { generatePaymentLinkForBooking } from '@adornis/buchungsheld-db/api/booking.js';
import { getParticipationsByID } from '@adornis/buchungsheld-db/api/participation-helper.js';
import { getStammdatenByUserAndInstitution } from '@adornis/buchungsheld-db/api/stammdaten.js';
import { BHOrderNr } from '@adornis/buchungsheld-db/entities/BHPayment.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { getProductPrice } from '../payment/default.js';
import { createPaymentFromParticipations } from '../payment/helper.js';

export const paymentPluginDefaultCreatePayment = registerMutation({
  type: () => String,
  operationName: 'paymentPluginDefaultCreatePayment',
  params: [
    { type: () => String, name: 'institutionID' },
    { type: () => [Participation], name: 'participationIds' },
    { type: () => String, name: 'paymentMethods' },
  ],
  resolve: (
    institutionID: string,
    _participations: NarrowedEntity<Participation, { _id: 1; productID: 1 }>[],
    paymentMethod: 'card' | 'paypal',
  ) => {
    return async () => {
      const participations = await getParticipationsByID(_participations.map(pa => pa._id))({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
        productPriceForUser: { unitAmount: 1, currency: 1 },
      });

      const user = await getCurrentUserServer();
      if (!user) throw new Error('user should be loggedIn');

      // create Payment
      const stammdaten = await getStammdatenByUserAndInstitution(user._id, institutionID)(UserStammdaten.allFields);
      if (!stammdaten) throw new Error('stammdaten not found');
      const payment = await createPaymentFromParticipations(institutionID, participations, getProductPrice);
      payment.orderNr = await BHOrderNr.create(institutionID);
      payment.userReference.userID = user._id;

      const paymentID = await payment.create();
      logger.info({ paymentID }, 'payment created');

      return paymentID;
    };
  },
});

export const paymentPluginDefaultGetPaymentLink = registerMutation({
  type: () => String,
  operationName: 'paymentPluginDefaultGetPaymentLink',
  params: [
    { type: () => String, name: 'paymentID' },
    { type: () => String, name: 'paymentMethod' },
  ],
  resolve: (paymentID: string, paymentMethod: 'card' | 'paypal') => {
    return async () => {
      // FIXME: this triggers the creation of another BHPayment (only one payment should be created)
      const paymentLink = generatePaymentLinkForBooking(paymentID, [paymentMethod]);
      return paymentLink;
    };
  },
});
